import { Navigate, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

/**
 * PermissionGuard - Protege rotas baseado em permissões do usuário
 * Uso: <PermissionGuard permission="view_products"><Component /></PermissionGuard>
 */
const PermissionGuard = ({ permission, children }) => {
  const navigate = useNavigate();
  const [hasPermission, setHasPermission] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkPermission = () => {
      try {
        // Buscar dados do usuário do localStorage
        const userStr = localStorage.getItem('user');
        if (!userStr) {
          setHasPermission(false);
          setIsLoading(false);
          return;
        }

        const user = JSON.parse(userStr);
        const userRole = user.role;
        
        // Super Admin, Owner e Admin têm acesso total
        if (userRole === 'superadmin' || userRole === 'owner' || userRole === 'admin') {
          setHasPermission(true);
          setIsLoading(false);
          return;
        }

        // Buscar permissões do objeto user.permissions
        const userPermissions = user.permissions || {};

        // 🔓 "Acesso Completo" - flag global do cargo, cobre também módulos futuros
        if (userPermissions?.all === true) {
          setHasPermission(true);
          setIsLoading(false);
          return;
        }
        
        // Mapear permissão requerida para o formato do backend
        // Ex: "view_products" -> products.view
        // Ex: "view_dashboard" -> dashboard.view
        const [action, module] = permission.includes('_') 
          ? [permission.split('_')[0], permission.split('_').slice(1).join('_')]
          : ['view', permission];
        
        // Mapear nomes especiais
        const moduleMap = {
          'products': 'products',
          'stock': 'stock',
          'sales': 'sales',
          'financial': 'financial',
          'fiscal': 'fiscal',
          'collaborators': 'collaborators',
          'roles': 'roles',
          'settings': 'settings',
          'dashboard': 'dashboard',
          'reports': 'reports',
          'cash': 'cash_register',
          'register': 'cash_register',
          'totem': 'totem',
          'devices': 'devices',
          'payments': 'payments',
          'kitchen_labels': 'kitchen_labels',
          'clients': 'clients'
        };
        
        const mappedModule = moduleMap[module] || module;
        
        // Verificar permissão no formato: userPermissions[module][action]
        const hasAccess = userPermissions[mappedModule]?.[action] === true || 
                         userPermissions[mappedModule]?.edit === true || // edit implica view
                         userPermissions[mappedModule]?.manage === true; // manage implica tudo

        // console.log(`🔒 PermissionGuard: ${permission} -> ${mappedModule}.${action} = ${hasAccess}`);
        
        setHasPermission(hasAccess);
        setIsLoading(false);
      } catch (error) {
        console.error('Erro ao verificar permissão:', error);
        setHasPermission(false);
        setIsLoading(false);
      }
    };

    checkPermission();
  }, [permission]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verificando permissões...</p>
        </div>
      </div>
    );
  }

  if (!hasPermission) {
    // Redirecionar para Welcome ao invés de mostrar tela de acesso negado
    toast.error('Você não tem permissão para acessar esta página');
    return <Navigate to="/admin/welcome" replace />;
  }

  return children;
};

export default PermissionGuard;
